// ============================================================================
// CANCUN100 — DescriptivoBoton.js  (Bloque 09 v3: botón "📄 PDF" + panel "Descriptivo del tour")
// ----------------------------------------------------------------------------
// Va al final de cada fila de las listas de precios (Sala / Top Tours, agente y admin).
// Al abrir lee `descriptivos_contenido` (sin netos) y el SUGERIDO que le corresponde al rol:
//   · Top Tours → top_tours_lista.precio_adulto/menor (agentes TT, Maggie, Marco)
//   · Sala      → sala_lista.precio_adulto/menor      (agentes Sala, Marco, Tere, Polo)
// "Con precio": adulto/menor arrancan en el sugerido y son editables (arriba o abajo).
// PROTECCIÓN DE NETO (todos los roles): el neto se lee y se compara SOLO en memoria (useRef);
// nunca se pinta, nunca va al `data` del PDF y nunca a console.*. Debajo del neto → aviso rojo
// y "Generar" bloqueado. Sin neto cargado → no se permite bajar del sugerido.
// Leyendas de precio (promoción del mes, por persona, apartado): Top Tours = obligatorias con 🔒;
// Sala y admins = libres. Genera en el navegador (Premium si hay ficha, Básico si no) y descarga
// "INFO {nombre}.pdf". Sin RPC ni cupo. Cero escrituras en la base.
// ============================================================================
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabaseClient';
import { MARCA_C100 } from './marcaC100';
import { prepararFotosPdf } from './fotosPdf';
import { leyendasPara, mesCancun } from './leyendasPrecio';
import {
  IDIOMAS, SECCIONES, TOGGLES_COMPLETO, TOGGLES_RESUMIDO, idiomasDisponibles,
  precioPdf, errorPrecio, armarPremium, armarBasico, armarTarjeta, nombreArchivo,
} from './armarDescriptivo';
import { contactoPorCanal } from '../contacto';
import { RADIO, RADIO_TARJETA } from '../tema';
import Segmentado from '../Segmentado';
import Stepper from '../Stepper'; // APP 2/05 · F1

const fmtPrecio = (n, moneda) => (Number(n) > 0 ? `${moneda === 'USD' ? 'USD $' : '$'}${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');
const LEYENDAS_UI = [
  ['promo', 'Banda "Promoción del mes"'],
  ['porPersona', '"Precio por persona"'],
  ['apartado', 'Leyenda de apartado (24 h)'],
];

export default function DescriptivoBoton({ C, producto, opcionesPrecio = [], loggedAgent = null, esAgente = false, leyendasBloqueadas = false }) {
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [fila, setFila] = useState(null);           // descriptivos_contenido (null en temporales o sin fila)
  const [sugeridos, setSugeridos] = useState({});   // { tt:{ad,men,moneda}, sala:{ad,men,moneda} } — públicos
  const netos = useRef({});                         // { tt:{ad,men}, sala:{ad,men} } — SOLO para comparar; nunca se pinta
  const [tasa, setTasa] = useState(null);
  const [asesor, setAsesor] = useState({ nombre: '', email: '' });
  const [idioma, setIdioma] = useState('es');
  const [conPrecio, setConPrecio] = useState(false);
  const [fuente, setFuente] = useState(opcionesPrecio[0] || 'sala');
  const [precioAd, setPrecioAd] = useState('');
  const [precioMen, setPrecioMen] = useState('');
  const [ley, setLey] = useState({ promo: true, porPersona: true, apartado: false });
  const [contenido, setContenido] = useState('completo');
  const [tog, setTog] = useState(TOGGLES_COMPLETO);
  const [notas, setNotas] = useState('');
  const [generando, setGenerando] = useState(false);
  // Bloque 13: diseño "Tarjeta promo" (Top Tours) o "Ficha completa"; lugares por día de la tarjeta.
  const puedeTarjeta = opcionesPrecio.includes('tt');
  const [diseno, setDiseno] = useState('ficha');
  const [lugares, setLugares] = useState('25');
  const [regularSala, setRegularSala] = useState(null); // público de Sala (para "PRECIO REGULAR" tachado)
  const [msg, setMsg] = useState(null);

  const esTemporal = !!producto?.es_temporal || /^TM[PS]-/.test(String(producto?.sku || ''));
  // Bloque 18: temporal de Sala (TMS-…) → su precio y neto vienen de sala_lista (la vista ya trae los temporales).
  const esTempSala = /^TMS-/.test(String(producto?.sku || ''));

  const ponerSugerido = (sug, f) => {
    const s = sug[f];
    setPrecioAd(s && Number(s.ad) > 0 ? String(s.ad) : '');
    setPrecioMen(s && Number(s.men) > 0 ? String(s.men) : '');
  };

  const abrir = async () => {
    setAbierto(true); setCargando(true); setMsg(null);
    setIdioma('es'); setConPrecio(false); setContenido('completo'); setTog(TOGGLES_COMPLETO); setNotas('');
    setLey(leyendasBloqueadas ? { promo: true, porPersona: true, apartado: true } : { promo: true, porPersona: true, apartado: false });
    setDiseno(puedeTarjeta && leyendasBloqueadas ? 'tarjeta' : 'ficha'); setLugares('25'); setRegularSala(null);
    const sku = producto.sku;
    const q = (p) => p.then((r) => (r.error ? null : r.data)).catch(() => null);
    const quiereTT = opcionesPrecio.includes('tt');
    const quiereSala = (opcionesPrecio.includes('sala') && (!esTemporal || esTempSala)) || (puedeTarjeta && !esTemporal); // la tarjeta usa el público de Sala como "precio regular"
    const [ficha, tt, sala, netoSala, prov, tasaDia, ag] = await Promise.all([
      esTemporal ? null : q(supabase.from('descriptivos_contenido').select('*').eq('sku', sku).maybeSingle()),
      quiereTT ? q(supabase.from('top_tours_lista').select('precio_adulto,precio_menor,moneda,precio_neto_adulto,precio_neto_menor').eq('sku', sku).maybeSingle()) : null,
      quiereSala ? q(supabase.from('sala_lista').select('precio_adulto,precio_menor,moneda,precio_neto_adulto,precio_neto_menor').eq('sku', sku).maybeSingle()) : null,
      opcionesPrecio.includes('sala') && !esTemporal ? q(supabase.from('productos').select('precio_neto_adulto,precio_neto_menor').eq('sku', sku).maybeSingle()) : null,
      producto.proveedor_codigo ? q(supabase.from('proveedores').select('tasa_usd').eq('codigo', producto.proveedor_codigo).maybeSingle()) : null,
      q(supabase.from('tasas_cambio').select('tasa_usd').eq('proveedor', 'Tasa del Día').maybeSingle()),
      // Bloque 12 §1f: correo PÚBLICO del agente (email_publico). El teléfono personal nunca se lee ni se publica.
      esAgente && loggedAgent?.id != null ? q(supabase.from('agentes').select('nombre,email_publico').eq('id', loggedAgent.id).maybeSingle()) : null,
    ]);
    const sug = {
      ...(tt ? { tt: { ad: tt.precio_adulto, men: tt.precio_menor, moneda: tt.moneda || producto.moneda } } : {}),
      ...(sala && opcionesPrecio.includes('sala') ? { sala: { ad: sala.precio_adulto, men: sala.precio_menor, moneda: sala.moneda || producto.moneda } } : {}),
    };
    setRegularSala(sala && Number(sala.precio_adulto) > 0 ? Number(sala.precio_adulto) : null);
    netos.current = {
      tt: tt ? { ad: tt.precio_neto_adulto, men: tt.precio_neto_menor } : null,
      // Bloque 18: el neto de Sala sale de sala_lista (incluye temporales TMS); si la vista no lo trae, de la biblia.
      sala: !opcionesPrecio.includes('sala') ? null
        : (sala && sala.precio_neto_adulto != null) ? { ad: sala.precio_neto_adulto, men: sala.precio_neto_menor }
        : netoSala ? { ad: netoSala.precio_neto_adulto, men: netoSala.precio_neto_menor } : null,
    };
    const f0 = opcionesPrecio.find((k) => sug[k]) || opcionesPrecio[0] || 'sala';
    setFila(ficha || null);
    setSugeridos(sug);
    setFuente(f0);
    ponerSugerido(sug, f0);
    setTasa(Number(prov?.tasa_usd) || Number(tasaDia?.tasa_usd) || null);
    setAsesor({ nombre: (ag && ag.nombre) || loggedAgent?.nombre || '', email: (ag && String(ag.email_publico || '').trim()) || '' });
    setCargando(false);
  };

  const elegirContenido = (v) => { setContenido(v); setTog(v === 'resumido' ? TOGGLES_RESUMIDO : TOGGLES_COMPLETO); };
  const elegirFuente = (f) => { setFuente(f); ponerSugerido(sugeridos, f); };

  const sug = sugeridos[fuente] || null;
  const moneda = (sug && sug.moneda) || producto.moneda || 'MXN';
  const hayMenor = !!sug && Number(sug.men) > 0;
  const neto = netos.current[fuente] || {};
  const errAd = conPrecio ? errorPrecio(precioAd, neto.ad, sug && sug.ad) : null;
  const errMen = conPrecio && hayMenor ? errorPrecio(precioMen, neto.men, sug && sug.men) : null;
  const faltaPrecio = conPrecio && !(Number(precioAd) > 0);
  const tarjetaSinPrecio = diseno === 'tarjeta' && !conPrecio;
  const bloqueado = generando || cargando || !!errAd || !!errMen || faltaPrecio || tarjetaSinPrecio;
  const editado = !!sug && (Number(precioAd) !== Number(sug.ad) || (hayMenor && Number(precioMen) !== Number(sug.men)));

  const generar = async () => {
    if (bloqueado) return;
    setGenerando(true); setMsg(null);
    try {
      const marca = {
        ...MARCA_C100,
        // Bloque 12 §1f: nombre y apellido completos + correo público del agente; teléfono y WhatsApp de la OFICINA.
        // Sin correo público → nombre + datos de oficina. El footer legal no cambia.
        contacto: {
          ...MARCA_C100.contacto,
          // Bloque 26: WhatsApp por canal (precio Top Tours → "Aventuras con Pao"; Sala → el oficial). Nunca el del agente.
          whatsapp: contactoPorCanal(fuente === 'tt' ? 'top_tours' : 'sala').whatsappE164,
          nombre: asesor.nombre || MARCA_C100.nombre,
          email: asesor.email || MARCA_C100.contacto.email,
        },
      };
      const esPremium = !!(fila && fila.tiene_ficha);
      const idiomaPdf = esPremium ? idioma : 'es'; // el Básico es en español
      const flags = leyendasBloqueadas ? { promo: true, porPersona: true, apartado: true } : ley;
      const precio = conPrecio
        ? precioPdf({ ad: precioAd, men: hayMenor ? precioMen : null, moneda }, tasa, leyendasPara(idiomaPdf, moneda, flags))
        : { mostrar: false };
      const [{ pdf }, Premium, Basico] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../pdf/descriptivo/PDFPremium2'),
        import('../pdf/descriptivo/PDFDescriptivo'),
      ]);
      let data, blob;
      if (diseno === 'tarjeta') {
        // Bloque 13: Tarjeta promo de 1 hoja (siempre con precio). El precio regular = público de Sala, solo si es mayor.
        const Tarjeta = await import('../pdf/descriptivo/PDFTarjetaTT');
        data = armarTarjeta({ fila, idioma: esPremium ? idioma : 'es', marca, precio, tasa, regular: regularSala, lugares, producto, iconoDe: Premium.iconoHighlight });
        data.mesIdx = mesCancun();
        data.tour = { nombre: data.nombre };
        if (data.fotos.length) data.fotos = await prepararFotosPdf(data.fotos.map((x, i) => ({ ...x, orden: i + 1 })));
        if (data.portada) data.portada = (await prepararFotosPdf([{ orden: 0, url: data.portada }]))[0]?.url || data.portada;
        blob = await pdf(React.createElement(Tarjeta.default, { data })).toBlob();
      } else if (esPremium) {
        data = armarPremium({ fila, idioma, tog, marca, precio, notasAsesor: notas, producto });
        data.imagenes = await prepararFotosPdf(data.imagenes); // reduce las fotos en el navegador (peso)
        blob = await pdf(React.createElement(Premium.default, { data })).toBlob();
      } else {
        data = armarBasico({ fila, tog, marca, precio, notasAsesor: notas, producto });
        blob = await pdf(React.createElement(Basico.default, { data })).toBlob();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = nombreArchivo(data.tour.nombre);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMsg({ ok: true, t: `PDF generado (${(blob.size / 1024 / 1024).toFixed(2)} MB).` });
    } catch (e) {
      console.error('[descriptivo] no se pudo generar el PDF:', e && e.message); // sin datos de precios
      setMsg({ ok: false, t: 'No se pudo generar el PDF. Intenta de nuevo.' });
    }
    setGenerando(false);
  };

  const plantilla = fila && fila.tiene_ficha ? 'Premium' : 'Básico';
  const idiomas = idiomasDisponibles(fila);
  const lbl = { fontSize: 14, color: C.muted, fontWeight: 600, marginBottom: 6, display: 'block' };
  const radio = (activo) => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: RADIO, border: `1px solid ${activo ? C.rosa : C.border}`, background: activo ? `${C.rosa}10` : 'transparent', cursor: 'pointer', fontSize: 12, color: C.text, marginBottom: 6 });
  const inputSt = (err) => ({ width: '100%', height: 40, boxSizing: 'border-box', background: C.dark, border: `1px solid ${err ? C.red : C.border}`, borderRadius: RADIO, padding: '0 12px', color: C.text, fontSize: 14, fontWeight: 700, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' });
  const aprox = (v) => (moneda === 'USD' && tasa && Number(v) > 0 ? `≈ $${Math.round(Number(v) * tasa).toLocaleString('es-MX')} MXN` : '');

  return (
    <>
      <button type="button" onClick={abrir} title="Descriptivo del tour en PDF"
        style={{ background: `${C.blue}15`, color: C.blue, border: `1px solid ${C.blue}40`, borderRadius: RADIO, height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--au-font-text)', whiteSpace: 'nowrap' }}>📄 PDF</button>
      {/* Portal a <body>: las tarjetas de la lista tienen backdrop-filter/overflow que recortarían un fixed. */}
      {abierto && createPortal(
        <div onClick={() => !generando && setAbierto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="au-glass" style={{ background: C.card || C.surface, width: '100%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto', padding: 20, textAlign: 'left', whiteSpace: 'normal' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Descriptivo del tour</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginTop: 2 }}>{producto.nombre}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Ref. {producto.sku}{!cargando && <> · Plantilla: <strong>{plantilla}</strong></>}</div>
              </div>
              <button type="button" onClick={() => setAbierto(false)} disabled={generando} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            {cargando ? <div style={{ padding: 30, textAlign: 'center', color: C.muted }}>Cargando ficha…</div> : (<>
              {idiomas.length > 1 && (
                <div style={{ marginBottom: 14 }}>
                  <span style={lbl}>Idioma</span>
                  <Segmentado valor={idioma} opciones={IDIOMAS.filter(([k]) => idiomas.includes(k))} onChange={setIdioma} />
                </div>
              )}

              {/* ── PRECIO ── */}
              <div style={{ marginBottom: 14 }}>
                <span style={lbl}>Precio</span>
                <label style={radio(!conPrecio)}><input type="radio" name={`pr-${producto.sku}`} checked={!conPrecio} onChange={() => setConPrecio(false)} /> Sin precio</label>
                <label style={radio(conPrecio)}>
                  <input type="radio" name={`pr-${producto.sku}`} checked={conPrecio} disabled={!sug} onChange={() => setConPrecio(true)} />
                  <span style={{ flex: 1 }}>Con precio</span>
                  {!sug && <span style={{ fontSize: 12, color: C.muted }}>sin precio cargado</span>}
                </label>
                {conPrecio && sug && (
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: RADIO_TARJETA, padding: 12, marginTop: 4 }}>
                    {opcionesPrecio.filter((k) => sugeridos[k]).length > 1 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Partir del sugerido de:</div>
                        <Segmentado valor={fuente} opciones={opcionesPrecio.filter((k) => sugeridos[k]).map((k) => [k, k === 'tt' ? 'Top Tours' : 'Sala'])} onChange={elegirFuente} />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 160px' }}>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Adulto ({moneda}) · sugerido {fmtPrecio(sug.ad, moneda)}</div>
                        <input type="number" min="0" step="0.01" value={precioAd} onChange={(e) => setPrecioAd(e.target.value)} style={inputSt(errAd)} />
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{aprox(precioAd)}</div>
                        {errAd && <div style={{ fontSize: 12, color: C.red, fontWeight: 700, marginTop: 3 }}>⚠ {errAd}</div>}
                      </div>
                      {hayMenor && (
                        <div style={{ flex: '1 1 160px' }}>
                          <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Menor ({moneda}) · sugerido {fmtPrecio(sug.men, moneda)}</div>
                          <input type="number" min="0" step="0.01" value={precioMen} onChange={(e) => setPrecioMen(e.target.value)} style={inputSt(errMen)} />
                          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{aprox(precioMen)}</div>
                          {errMen && <div style={{ fontSize: 12, color: C.red, fontWeight: 700, marginTop: 3 }}>⚠ {errMen}</div>}
                        </div>
                      )}
                    </div>
                    {editado && <button type="button" className="au-btn au-btn--ghost" onClick={() => ponerSugerido(sugeridos, fuente)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12, marginTop: 8 }}>↺ Volver al sugerido</button>}
                    <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                      {LEYENDAS_UI.map(([k, l]) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, cursor: leyendasBloqueadas ? 'not-allowed' : 'pointer', padding: '2px 0', opacity: leyendasBloqueadas ? 0.8 : 1 }}>
                          <input type="checkbox" checked={leyendasBloqueadas ? true : !!ley[k]} disabled={leyendasBloqueadas} onChange={(e) => setLey((x) => ({ ...x, [k]: e.target.checked }))} />
                          {l}{leyendasBloqueadas && ' 🔒'}
                        </label>
                      ))}
                      {leyendasBloqueadas && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>En Top Tours estas leyendas salen siempre con el precio.</div>}
                    </div>
                  </div>
                )}
              </div>

              {puedeTarjeta && (
                <div style={{ marginBottom: 14 }}>
                  <span style={lbl}>Diseño</span>
                  <Segmentado valor={diseno} opciones={[['tarjeta', 'Tarjeta promo'], ['ficha', 'Ficha completa']]} onChange={(d) => { setDiseno(d); if (d === 'tarjeta') setConPrecio(!!sug); }} />
                  {diseno === 'tarjeta' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: C.text }}>
                      Promo limitada a
                      <Stepper chico ancho={130} value={lugares} min={1} max={99} label="lugares por día" onChange={(n) => setLugares(String(n))} />
                      lugares por día
                    </div>
                  )}
                  {tarjetaSinPrecio && <div style={{ fontSize: 12, color: C.red, fontWeight: 700, marginTop: 6 }}>⚠ La tarjeta promo requiere precio.</div>}
                </div>
              )}

              {diseno !== 'tarjeta' && (<>
              <div style={{ marginBottom: 14 }}>
                <span style={lbl}>Contenido</span>
                <Segmentado valor={contenido} opciones={[['completo', 'Completo'], ['resumido', 'Resumido']]} onChange={elegirContenido} style={{ marginBottom: 14 }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 4 }}>
                  {SECCIONES.map(([k, l]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, cursor: 'pointer', padding: '3px 0' }}>
                      <input type="checkbox" checked={!!tog[k]} onChange={(e) => setTog((t) => ({ ...t, [k]: e.target.checked }))} />
                      {l}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Incluye / No incluye siempre se muestran.</div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <span style={lbl}>Notas del asesor (opcional)</span>
                <textarea rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Salen como “Consejos del asesor”."
                  style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: RADIO, padding: '9px 12px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              </>)}

              {msg && <div className={`au-banner ${msg.ok ? 'au-banner--success' : 'au-banner--error'}`} style={{ marginBottom: 14, fontSize: 12 }}>{msg.t}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="au-btn au-btn--ghost" onClick={() => setAbierto(false)} disabled={generando}>Cerrar</button>
                <button type="button" className="au-btn au-btn--primary" onClick={generar} disabled={bloqueado}>{generando ? 'Generando…' : 'Generar PDF'}</button>
              </div>
            </>)}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
